import { Menu, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { ROUTES } from '@/shared/config/routes';
import { useScrolled } from '@/shared/hooks';
import { cn } from '@/shared/lib';

import { NotificationsMenu } from './NotificationsMenu';
import { UserMenu, type AppAudience } from './UserMenu';

type AppHeaderProps = {
  /** Which dashboard this bar sits on. */
  audience: AppAudience;
  /** Section name shown on the master dashboard at desktop width. */
  title?: string;
  onMobileMenuToggle?: () => void;
};

/** Client-only tabs shown in the centre of the bar at desktop width. */
const CLIENT_TABS = [
  { to: ROUTES.client.bookings, labelKey: 'clientNav.myBookings' },
  { to: ROUTES.explore, labelKey: 'clientNav.findMaster' },
];

/**
 * Top bar for both dashboards.
 *
 * The generated version decided whether to show the wordmark by reading
 * `window.innerWidth` during render — not reactive, and it would crash the
 * prerenderer. The wordmark is now hidden with a CSS breakpoint, which is what
 * the surrounding markup was already doing anyway.
 */
export const AppHeader = ({ audience, title, onMobileMenuToggle }: AppHeaderProps) => {
  const { t } = useTranslation('app');
  const { pathname } = useLocation();
  const scrolled = useScrolled();
  const isMaster = audience === 'master';

  return (
    <header
      className={cn(
        'sticky top-0 z-40 w-full transition-all duration-200',
        scrolled
          ? 'border-b border-gray-100 bg-white/90 shadow-sm backdrop-blur-md'
          : 'border-b border-transparent bg-white',
      )}
    >
      <div
        className={cn(
          'flex h-[72px] items-center justify-between px-4 md:px-8',
          !isMaster && 'mx-auto max-w-7xl',
        )}
      >
        <div className="flex items-center gap-4">
          {isMaster && (
            <button
              type="button"
              onClick={onMobileMenuToggle}
              aria-label={t('header.openMenu')}
              className="-ml-2 rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 md:hidden"
            >
              <Menu size={24} />
            </button>
          )}

          {isMaster && (
            <h2 className="hidden font-heading text-xl font-bold text-gray-900 md:block">
              {title ?? t('nav.dashboard')}
            </h2>
          )}

          <Link
            to={isMaster ? ROUTES.app.root : ROUTES.client.root}
            className={cn('flex items-center gap-2', isMaster && 'md:hidden')}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-black text-white">
              <Sparkles size={16} className="text-warning" />
            </div>
            <span className="font-heading text-xl font-black tracking-tight text-dark">
              Krasa<span className="text-primary">Hub</span>
            </span>
          </Link>
        </div>

        {!isMaster && (
          <nav className="hidden flex-1 justify-center gap-2 md:flex">
            {CLIENT_TABS.map(({ to, labelKey }) => (
              <Link
                key={to}
                to={to}
                className={cn(
                  'rounded-xl px-4 py-2 text-sm font-bold transition-colors',
                  pathname.startsWith(to)
                    ? 'bg-primary text-white'
                    : 'text-gray-600 hover:bg-gray-50',
                )}
              >
                {t(labelKey)}
              </Link>
            ))}
          </nav>
        )}

        <div className="flex items-center gap-2 md:gap-4">
          {isMaster && <NotificationsMenu />}
          {isMaster && <div className="hidden h-6 w-px bg-gray-200 md:block" aria-hidden="true" />}
          <UserMenu audience={audience} />
        </div>
      </div>
    </header>
  );
};
